import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useCallback, useState } from 'react';

import { calculateDateRange } from './dateRangeUtils';
import type { UsePipelineLogsDataParams, SearchParams } from '../types';

import {
  fetchPipelineLogs,
  MONITOR_PAGE_SIZE,
  pipelineLogsQueryKeys,
  transformOtelLog,
  type PipelineLogsFilters
} from '~/api/monitorService';
import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import { useLogsSearchQuery } from '~/generated/graphql';

export { pipelineLogsQueryKeys };

function buildFilters(params: SearchParams): PipelineLogsFilters {
  let pipelineName = params.pipelineName || '';
  let searchType: PipelineLogsFilters['searchType'];

  if (pipelineName) {
    if (pipelineName.startsWith('*') && pipelineName.endsWith('*')) {
      const stripped = pipelineName.slice(1, -1);
      if (stripped) {
        pipelineName = stripped;
        searchType = 'LIKE';
      } else {
        // wildcard-only (e.g. "**") → treat as no filter
        pipelineName = '';
      }
    } else {
      searchType = 'EXACT';
    }
  }

  return {
    ...(params.pipelineKey && { pipelineKey: params.pipelineKey }),
    ...(pipelineName && { pipelineName }),
    ...(pipelineName && searchType && { searchType }),
    ...(params.logMessage && { logMessage: params.logMessage }),
    ...(params.pipelineVersion && { pipelineVersion: params.pipelineVersion }),
    ...(params.logType &&
      params.logType !== 'ALL SOURCES' && { logType: params.logType }),
    ...(params.level &&
      params.level !== 'ALL SEVERITIES' && { level: params.level })
  };
}

export function usePipelineLogsData({
  realm,
  environment,
  searchParams
}: UsePipelineLogsDataParams) {
  const { treatments } = useFeatureFlag([
    featureFlagConstants.MONITOR_MIGRATE_PIPELINE_LOGS_TO_MONITOR_SERVICE
  ]);

  const isMonitorServiceEnabled =
    treatments[
      featureFlagConstants.MONITOR_MIGRATE_PIPELINE_LOGS_TO_MONITOR_SERVICE
    ]?.treatment === 'on';

  const { dateFrom, dateTo } = useMemo(() => {
    if (searchParams.dateFrom && searchParams.dateTo) {
      return {
        dateFrom: decodeURIComponent(searchParams.dateFrom),
        dateTo: decodeURIComponent(searchParams.dateTo)
      };
    }
    return calculateDateRange(
      searchParams.time || '5 Minutes',
      searchParams.specific
    );
  }, [
    searchParams.time,
    searchParams.specific,
    searchParams.dateFrom,
    searchParams.dateTo
  ]);

  const graphqlVariables = useMemo(
    () => ({
      realm,
      environment,
      dateFrom,
      dateTo,
      ...buildFilters(searchParams),
      ...(searchParams.direction && {
        sortBy: searchParams.direction.toLowerCase()
      })
    }),
    [realm, environment, dateFrom, dateTo, searchParams]
  );

  const {
    data: graphqlData,
    loading: graphqlLoading,
    error: graphqlError,
    fetchMore: graphqlFetchMore
  } = useLogsSearchQuery({
    variables: graphqlVariables,
    skip: isMonitorServiceEnabled || !realm || !environment,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const graphqlLogs = useMemo(
    () =>
      graphqlData?.logsSearch?.logs
        ?.filter(log => log !== null)
        .map(log => ({
          id: log.id,
          key: log.id,
          pipelineName: log.pipelineName,
          pipelineKey: log.pipelineKey || '',
          logMessage: log.logMessage || '',
          timestamp: log.timestamp,
          timestampMillis: log.timestampMillis || '',
          realm: log.realm || '',
          logLevel: log.logLevel || '',
          // eslint-disable-next-line no-underscore-dangle
          __typename: log.__typename
        })) || [],
    [graphqlData]
  );

  const graphqlCount = graphqlData?.logsSearch?.count || 0;

  const [graphqlLoadingMore, setGraphqlLoadingMore] = useState(false);

  const {
    data: monitorData,
    isLoading: monitorLoading,
    isFetchingNextPage: monitorLoadingMore,
    error: monitorError,
    hasNextPage: monitorHasNextPage,
    fetchNextPage: monitorFetchNextPage
  } = useInfiniteQuery({
    queryKey: pipelineLogsQueryKeys.list({
      realm,
      environment,
      filters: buildFilters(searchParams),
      dateFrom,
      dateTo,
      limit: MONITOR_PAGE_SIZE,
      order: searchParams.direction === 'ASC' ? 'ascending' : 'descending'
    }),
    queryFn: ({ pageParam }) =>
      fetchPipelineLogs({
        realm,
        environment,
        filters: buildFilters(searchParams),
        dateFrom,
        dateTo,
        limit: MONITOR_PAGE_SIZE,
        order: searchParams.direction === 'ASC' ? 'ascending' : 'descending',
        cursor: pageParam
      }),
    getNextPageParam: lastPage =>
      lastPage.pagination?.hasNext ? lastPage.pagination.nextCursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: isMonitorServiceEnabled && !!realm && !!environment,
    staleTime: 0,
    refetchOnWindowFocus: false
  });

  const monitorLogs = useMemo(() => {
    if (!monitorData?.pages) return [];

    return monitorData.pages.flatMap((page, pageIndex) =>
      (page.data || []).map((log, logIndex) =>
        transformOtelLog(log, pageIndex * MONITOR_PAGE_SIZE + logIndex)
      )
    );
  }, [monitorData]);

  const logs = isMonitorServiceEnabled ? monitorLogs : graphqlLogs;
  const count = isMonitorServiceEnabled
    ? (monitorData?.pages[0]?.total ?? 0)
    : graphqlCount;
  const loading = isMonitorServiceEnabled ? monitorLoading : graphqlLoading;
  const loadingMore = isMonitorServiceEnabled
    ? monitorLoadingMore
    : graphqlLoadingMore;
  const error = isMonitorServiceEnabled
    ? (monitorError as Error | null)
    : graphqlError;

  const hasMore = useMemo(() => {
    if (isMonitorServiceEnabled) {
      return monitorHasNextPage ?? false;
    }
    const logsLength = graphqlLogs.length;
    return logsLength > 0 && logsLength < graphqlCount;
  }, [
    isMonitorServiceEnabled,
    monitorHasNextPage,
    graphqlLogs.length,
    graphqlCount
  ]);

  const handleLoadMore = useCallback(async () => {
    if (isMonitorServiceEnabled) {
      if (!monitorHasNextPage || monitorLoadingMore) return;
      await monitorFetchNextPage();
      return;
    }

    if (!graphqlLogs.length || graphqlLoadingMore || !hasMore) return;

    const lastLog = graphqlLogs[graphqlLogs.length - 1];
    setGraphqlLoadingMore(true);

    try {
      await graphqlFetchMore({
        variables: {
          ...graphqlVariables,
          afterId: lastLog.id,
          afterDate: lastLog.timestamp
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.logsSearch?.logs) return previousResult;

          return {
            ...previousResult,
            logsSearch: {
              ...previousResult.logsSearch,
              ...fetchMoreResult.logsSearch,
              logs: [
                ...(previousResult.logsSearch?.logs || []),
                ...fetchMoreResult.logsSearch.logs
              ]
            }
          };
        }
      });
    } finally {
      setGraphqlLoadingMore(false);
    }
  }, [
    isMonitorServiceEnabled,
    monitorHasNextPage,
    monitorLoadingMore,
    monitorFetchNextPage,
    graphqlLogs,
    graphqlLoadingMore,
    hasMore,
    graphqlFetchMore,
    graphqlVariables
  ]);

  return {
    logs,
    count,
    loading,
    loadingMore,
    hasMore,
    error,
    handleLoadMore
  };
}
